import { HasIdAndName, NestedOption, TreeSelectField, useComputed } from "@homebound/beam";
import { ListFieldState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useMemo } from "react";
import { NamedDevelopmentCohortsProjectsFragment } from "src/fragments/circularFragments.generated";
import { ProjectDocumentAssociationInput } from "src/generated/graphqlTypes";

export type ProjectWithChildren = {
  id: string;
  name: string;
  childrenRecursive: Array<{ id: string; name: string; parent?: { id: string } | null }>;
};

export type BoundAssociatedProjectsTreeSelectProps = {
  development?: NamedDevelopmentCohortsProjectsFragment;
  parentProject?: ProjectWithChildren;
  field: ListFieldState<ProjectDocumentAssociationInput>;
  readOnly?: boolean;
  expandAll?: boolean;
};

export function BoundAssociatedProjectsTreeSelect(props: BoundAssociatedProjectsTreeSelectProps) {
  const { development, parentProject, field, readOnly = false, expandAll = false } = props;

  // Build the tree options and flat project list based on parent type
  const { options, allProjectIds } = useMemo(() => {
    if (development) {
      // Development: 3-level tree (development -> cohort -> project)
      const options: NestedOption<HasIdAndName>[] = [
        {
          id: development.id,
          name: development.name,
          children: (development.cohorts ?? [])
            .filter((c) => !c.projects.isEmpty)
            .map((cohort) => ({
              id: cohort.id,
              name: cohort.name,
              children: cohort.projects.map((p) => ({ id: p.id, name: p.name })),
            })),
        },
      ];
      const allProjectIds = development.cohorts?.flatMap((c) => c.projects.map((p) => p.id)) ?? [];
      return { options, allProjectIds };
    } else if (parentProject) {
      // Hierarchy: nested tree including childrenRecursive grouped by level/parent
      const options: NestedOption<HasIdAndName>[] = [
        {
          id: parentProject.id,
          name: parentProject.name,
          children: buildProjectTree(parentProject.id, parentProject.childrenRecursive),
        },
      ];
      const allProjectIds = parentProject.childrenRecursive.map((p) => p.id);
      return { options, allProjectIds };
    }
    return { options: [], allProjectIds: [] };
  }, [development, parentProject]);

  // Currently selected project IDs (entries without delete flag)
  const selectedValues = useComputed(
    () => (field.value ?? []).filter((v) => !v.delete).map((v) => v.projectId),
    [field],
  );

  function onSelect({ leaf, all }: { leaf: { values: (string | number)[] }; all: { values: (string | number)[] } }) {
    // Dev path uses `leaf`: legacy behavior where only Projects (leaves) are associable, Development/Cohort are excluded.
    // Hierarchy path uses `all`: selecting a parent includes all nested levels (Subdivision/Lot/Structure).
    const selectedIds = parentProject ? all.values : leaf.values;
    const newSelectedIds = new Set(selectedIds as string[]);
    const previouslySelectedIds = new Set((field.value ?? []).filter((v) => !v.delete).map((v) => v.projectId));
    const newValue: ProjectDocumentAssociationInput[] = allProjectIds
      .map((projectId) => {
        if (newSelectedIds.has(projectId)) return { projectId, delete: false };
        if (previouslySelectedIds.has(projectId)) return { projectId, delete: true };
        return undefined;
      })
      .compact();
    field.set(newValue);
    field.maybeAutoSave();
  }

  return (
    <Observer>
      {() => (
        <TreeSelectField
          label="Projects"
          options={options}
          values={selectedValues}
          onSelect={onSelect}
          readOnly={readOnly}
          placeholder="Select Projects"
          {...(!expandAll ? { defaultCollapsed: true } : {})}
        />
      )}
    </Observer>
  );
}

function buildProjectTree(
  rootId: string,
  items: ProjectWithChildren["childrenRecursive"],
): NestedOption<HasIdAndName>[] {
  const byParentId = items.groupBy((p) => p.parent?.id ?? "");
  const getChildren = (parentId: string): NestedOption<HasIdAndName>[] =>
    (byParentId[parentId] ?? []).map((p) => ({ id: p.id, name: p.name, children: getChildren(p.id) }));
  return getChildren(rootId);
}
